import { ReactElement, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { isEqual } from 'lodash';
import { Card, CardContent, CardActions } from '@mui/material';
import { CreateDynamoDBTableRequest, UpdateDynamoDBTableRequest } from '@localstack/types';
import { useRoutes, useAwsEffect, useAwsGetter } from '@localstack/services';
import { PageTitle, ProgressButton, DynamoDBTableForm } from '@localstack/ui';
import { DEFAULT_DYNAMODB_ROUTES } from '@localstack/constants';
import { ReplicationGroupUpdateList } from 'aws-sdk/clients/dynamodb';

import { TableNavTabs } from './components';
import { DynamoDBProps } from './types';

/**
 * Helper for provisioned throughput equality (handles Description type)
 */
function provisionedThroughputEquals(
  a?: AWS.DynamoDB.ProvisionedThroughput,
  b?: AWS.DynamoDB.ProvisionedThroughputDescription,
) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    (a.ReadCapacityUnits ?? 0) === (b.ReadCapacityUnits ?? 0) &&
    (a.WriteCapacityUnits ?? 0) === (b.WriteCapacityUnits ?? 0)
  );
}

export const DynamoDBTableUpsert = ({
  Layout,
  clientOverrides,
  routes = DEFAULT_DYNAMODB_ROUTES,
}: DynamoDBProps): ReactElement => {
  const { goto } = useRoutes();
  const { tableName } = useParams<'tableName'>();

  const { createTable, updateTable, isLoading } = useAwsEffect('DynamoDB', ['createTable', 'updateTable'], {
    revalidate: ['listTables', 'describeTable'],
    clientOverrides,
  });

  const { data: table } = useAwsGetter('DynamoDB', 'describeTable', [{ TableName: tableName }], { clientOverrides });

  // Helper for payload construction
  const getUpdatePayload = useCallback(
    (formData: UpdateDynamoDBTableRequest, currentTable: AWS.DynamoDB.TableDescription): UpdateDynamoDBTableRequest => {
      const payload: UpdateDynamoDBTableRequest = {
        ...formData,
        TableName: formData.TableName,
      };

      // Strip unchanged ProvisionedThroughput
      const currentPT = currentTable.ProvisionedThroughput;
      const newPT = formData.ProvisionedThroughput;
      if (provisionedThroughputEquals(newPT, currentPT)) {
        delete payload.ProvisionedThroughput;
      }

      // Strip unchanged StreamSpecification
      const newSpec = formData.StreamSpecification;
      const currentSpec = currentTable.StreamSpecification;
      if (isEqual(newSpec, currentSpec)) {
        delete payload.StreamSpecification;
      }

      // Restructure ReplicaUpdates
      const currentReplicas = currentTable.Replicas?.map((r) => r.RegionName) ?? [];
      if (formData.ReplicaUpdates?.length) {
        const replicaUpdates: ReplicationGroupUpdateList = [];

        formData.ReplicaUpdates.forEach((update) => {
          if (update.Create?.RegionName && !currentReplicas.includes(update.Create.RegionName)) {
            replicaUpdates.push({ Create: update.Create });
          } else if (update.Delete?.RegionName && currentReplicas.includes(update.Delete.RegionName)) {
            replicaUpdates.push({ Delete: update.Delete });
          } else if (update.Update?.RegionName && currentReplicas.includes(update.Update.RegionName)) {
            replicaUpdates.push({ Update: update.Update });
          }
        });

        if (replicaUpdates.length > 0) {
          payload.ReplicaUpdates = replicaUpdates;
        } else {
          delete payload.ReplicaUpdates;
        }
      }

      return payload;
    },
    [],
  );

  const handleCreate = useCallback(
    async (data: CreateDynamoDBTableRequest) => {
      if (await createTable(data)) {
        goto(routes.RESOURCES_DYNAMODB);
      }
    },
    [createTable, goto, routes.RESOURCES_DYNAMODB],
  );

  const handleUpdate = useCallback(
    async (formData: UpdateDynamoDBTableRequest) => {
      const currentTable = table?.Table;
      if (!currentTable) return;

      const updatePayload = getUpdatePayload(formData, currentTable);

      await updateTable(updatePayload);
      goto(routes.RESOURCES_DYNAMODB_TABLE, { tableName });
    },
    [getUpdatePayload, goto, routes.RESOURCES_DYNAMODB_TABLE, table, tableName, updateTable],
  );

  return (
    <Layout
      title={
        <PageTitle
          title={tableName ? 'Edit Table' : 'Create Table'}
          breadcrumbs={[
            ['DynamoDB', () => goto(routes.RESOURCES_DYNAMODB)],
            [tableName, () => goto(routes.RESOURCES_DYNAMODB_TABLE, { tableName })],
            [tableName ? 'Update' : 'Create Table', null],
          ]}
        />
      }
      tabs={tableName && <TableNavTabs tableName={tableName} routes={routes} />}
      actions={
        <ProgressButton type="submit" form="update" variant="outlined" color="primary" loading={isLoading}>
          Submit
        </ProgressButton>
      }
    >
      <Card>
        <CardContent>
          <DynamoDBTableForm
            table={table?.Table ?? undefined}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            loading={isLoading}
            formId="update"
            // TODO: Add externalFields override for region dropdown if needed. externalFields is not currently a prop.
          />
        </CardContent>
        <CardActions>
          <ProgressButton type="submit" form="update" variant="contained" color="primary" loading={isLoading}>
            Submit
          </ProgressButton>
        </CardActions>
      </Card>
    </Layout>
  );
};
